import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Smartphone } from 'lucide-react';
import { PosCustomerPicker } from '@/components/pos/PosCustomerPicker.js';
import { CustomerStatusGate } from '@/components/common/CustomerStatusGate.js';
import { listTickets } from '@/lib/api/tickets.js';
import { listCustomerDevices, type CustomerDeviceHistoryItem } from '@/lib/api/customers.js';
import type { StoreSettings } from '@/lib/api/stores.js';
import { computeCartTotals, cartHasInventory, cartHasRepairsOnly } from '@/lib/pos/cart-math.js';
import { formatCurrency, formatRelativeTime } from '@/lib/format.js';
import { usePosCartStore } from '@/stores/pos-cart.js';

type PosCustomerPanelProps = {
  store: StoreSettings;
  onCheckout: () => void;
};

const OPEN_STATUSES = [
  'quote',
  'intake',
  'pending_sig',
  'in_queue',
  'diagnosing',
  'waiting_auth',
  'waiting_parts',
  'in_repair',
  'on_hold',
  'ready',
  'pending_pay',
].join(',');

export function PosCustomerPanel({ store, onCheckout }: PosCustomerPanelProps) {
  const navigate = useNavigate();
  const [checkoutPrompt, setCheckoutPrompt] = useState(false);
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const [flagAcked, setFlagAcked] = useState(false);

  const items = usePosCartStore((s) => s.items);
  const customer = usePosCartStore((s) => s.customer);
  const cartDiscount = usePosCartStore((s) => s.cartDiscount);
  const loyaltyApplied = usePosCartStore((s) => s.loyaltyApplied);
  const storeCreditApplied = usePosCartStore((s) => s.storeCreditApplied);
  const repairSelection = usePosCartStore((s) => s.repairSelection);
  const setCustomer = usePosCartStore((s) => s.setCustomer);
  const clearCart = usePosCartStore((s) => s.clearCart);

  const taxRate = Number(store.taxRate ?? 0);
  const taxMode = store.taxMode ?? 'parts_only';
  const totals = computeCartTotals(items, cartDiscount, taxRate, taxMode);
  const amountDue = totals.total - loyaltyApplied - storeCreditApplied;

  useEffect(() => {
    setFlagAcked(false);
  }, [customer?.id]);

  const openTicketsQuery = useQuery({
    queryKey: ['pos-open-tickets', customer?.id, customer?.phone],
    queryFn: () => {
      const params: Parameters<typeof listTickets>[0] = {
        status: OPEN_STATUSES,
        page: 1,
        pageSize: 5,
      };
      const search = customer?.phone ?? customer?.firstName;
      if (search) params.search = search;
      return listTickets(params);
    },
    enabled: Boolean(customer?.phone || customer?.firstName),
  });

  const customerDevicesQuery = useQuery({
    queryKey: ['customer-devices', customer?.id],
    queryFn: () => listCustomerDevices(customer!.id),
    enabled: Boolean(customer?.id),
    staleTime: 60_000,
  });

  const openTickets = useMemo(() => {
    if (!customer) return [];
    return (openTicketsQuery.data?.data ?? []).filter(
      (t) => t.customerDisplayName?.includes(customer.firstName) || true,
    );
  }, [customer, openTicketsQuery.data?.data]);

  function buildQuickTicketState(opts?: {
    device?: CustomerDeviceHistoryItem | null;
    newDevice?: boolean;
  }) {
    const repairs = items
      .filter((i) => i.type === 'repair')
      .map((i) => ({
        serviceName: i.serviceName!,
        quotedPrice: i.unitPrice,
        serviceId: i.serviceId,
      }));

    if (opts?.newDevice) {
      return {
        prefill: {
          repairs,
          customerId: customer?.id,
          fromPos: true,
        },
      };
    }

    if (opts?.device) {
      const device = opts.device;
      return {
        prefill: {
          makeId: device.makeId ?? undefined,
          makeName: device.makeName ?? undefined,
          modelId: device.modelId ?? undefined,
          modelName: device.modelName ?? device.customModelName ?? undefined,
          imei: device.lastImei ?? undefined,
          color: device.lastColor ?? undefined,
          serialNumber: device.lastSerial ?? undefined,
          passcodeType: device.lastPasscodeType ?? undefined,
          fromHistory: true,
          repairs,
          customerId: customer?.id,
          fromPos: true,
        },
      };
    }

    const selection = repairSelection ?? (items.find((i) => i.type === 'repair') ? {
      makeId: items.find((i) => i.type === 'repair')!.makeId!,
      makeName: items.find((i) => i.type === 'repair')!.makeName!,
      modelId: items.find((i) => i.type === 'repair')!.modelId!,
      modelName: items.find((i) => i.type === 'repair')!.modelName!,
    } : null);

    return {
      prefill: {
        makeId: selection?.makeId,
        makeName: selection?.makeName,
        modelId: selection?.modelId,
        modelName: selection?.modelName,
        repairs,
        customerId: customer?.id,
        fromPos: true,
      },
    };
  }

  function startRepairTicket() {
    const devices = customerDevicesQuery.data ?? [];
    if (customer?.id && devices.length > 0) {
      setDevicePickerOpen(true);
      return;
    }
    navigate('/tickets/quick', { state: buildQuickTicketState() });
  }

  function handleCheckoutClick() {
    if (store.posRequireCustomer && !customer) {
      return;
    }
    if (customer?.customerStatus === 'flagged' && !flagAcked) {
      return;
    }
    if (cartHasRepairsOnly(items) && !cartHasInventory(items)) {
      setCheckoutPrompt(true);
      return;
    }
    onCheckout();
  }

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 space-y-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          {customer ? (
            <>
              <CustomerStatusGate
                customer={customer}
                context="pos"
                vipLabel={store.vipLabel ?? null}
                flaggedLabel={store.flaggedLabel ?? null}
                onFlagAcknowledged={() => setFlagAcked(true)}
              />
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-slate-950">
                  {customer.firstName} {customer.lastName ?? ''}
                </div>
                <button
                  type="button"
                  onClick={() => setCustomer(null)}
                  className="text-xs font-semibold text-slate-500 hover:text-red-600"
                >
                  × Clear
                </button>
              </div>
              {customer.phone ? <div className="mt-1 text-sm text-slate-600">📱 {customer.phone}</div> : null}
              {customer.email ? <div className="text-sm text-slate-600">📧 {customer.email}</div> : null}
              <div className="mt-3 space-y-1 text-sm">
                <div>🎁 Loyalty: {Number(customer.loyaltyPointsBalance ?? 0)} pts</div>
                <div>💳 Store Credit: {formatCurrency(customer.storeCreditBalance ?? 0)}</div>
                {customer.lastVisitAt ? (
                  <div className="text-xs text-slate-500">
                    Last visit: {formatRelativeTime(customer.lastVisitAt)}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <PosCustomerPicker variant="card" />
          )}
        </div>

        <div className="space-y-2">
          <button
            type="button"
            onClick={startRepairTicket}
            className="w-full rounded-xl bg-teal-600 py-3 text-sm font-bold text-white hover:bg-teal-700"
          >
            🎫 Create Repair Ticket
          </button>
          <button
            type="button"
            onClick={() =>
              navigate('/inquiries/new', {
                state: customer?.id ? { customerId: customer.id } : undefined,
              })
            }
            className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-white hover:bg-primary/90"
          >
            📋 Create Inquiry / Quote
          </button>
        </div>

        {customer && openTickets.length ? (
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <h4 className="mb-2 text-xs font-bold uppercase text-slate-500">
              Open Tickets for {customer.firstName}
            </h4>
            <div className="space-y-2">
              {openTickets.map((ticket) => (
                <div key={ticket.id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold text-primary">{ticket.ticketNumber}</div>
                    <div className="truncate text-xs text-slate-500">
                      {ticket.deviceSummary ?? ticket.repairSummary ?? ticket.status}
                    </div>
                  </div>
                  <Link
                    to={`/tickets/${ticket.id}`}
                    className="shrink-0 rounded-lg border border-slate-300 px-2 py-1 text-xs font-bold"
                  >
                    Open
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Subtotal</span>
            <span className="font-medium text-slate-950">{formatCurrency(totals.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">
              {taxMode === 'parts_only' ? 'Parts Tax' : 'Tax'}
              {taxMode !== 'none' && taxRate > 0 ? ` (${(taxRate * 100).toFixed(1)}%)` : ''}
            </span>
            <span className="font-medium text-slate-950">{formatCurrency(totals.tax)}</span>
          </div>
          <div className="flex justify-between text-sm text-green-700">
            <span>Discount</span>
            <span className="font-medium">
              {totals.youSaved > 0 ? `−${formatCurrency(totals.youSaved)}` : formatCurrency(0)}
            </span>
          </div>
          <div className="border-t border-slate-200 pt-2 text-center">
            <div className="text-xs font-bold uppercase text-slate-500">Total</div>
            <div className="text-2xl font-black text-slate-950">{formatCurrency(amountDue)}</div>
          </div>
        </div>
        {store.posRequireCustomer && !customer ? (
          <p className="mb-2 text-center text-xs text-amber-700">Customer required for checkout</p>
        ) : null}
        <button
          type="button"
          disabled={items.length === 0 || (store.posRequireCustomer && !customer) || (customer?.customerStatus === 'flagged' && !flagAcked)}
          onClick={handleCheckoutClick}
          className="mb-2 w-full rounded-xl bg-green-600 py-3 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50"
        >
          💰 Checkout →
        </button>
        <button
          type="button"
          onClick={clearCart}
          className="w-full rounded-xl border border-slate-300 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
        >
          🔄 Clear Cart
        </button>
      </div>

      {checkoutPrompt ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-950">Repair services in cart</h3>
            <p className="mt-2 text-sm text-slate-600">
              Create a repair ticket or complete as a quick sale?
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setCheckoutPrompt(false);
                  onCheckout();
                }}
                className="rounded-xl bg-green-600 py-2.5 text-sm font-bold text-white"
              >
                Quick Sale
              </button>
              <button
                type="button"
                onClick={() => {
                  setCheckoutPrompt(false);
                  startRepairTicket();
                }}
                className="rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white"
              >
                Create Repair Ticket
              </button>
              <button
                type="button"
                onClick={() => setCheckoutPrompt(false)}
                className="rounded-xl border border-slate-300 py-2 text-sm font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {devicePickerOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-950">Which device needs repair?</h3>
            <div className="mt-4 max-h-80 space-y-2 overflow-y-auto">
              {(customerDevicesQuery.data ?? []).map((device) => (
                <button
                  key={[device.makeId, device.modelId, device.customModelName].join('|')}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-primary hover:bg-primary/5"
                  onClick={() => {
                    setDevicePickerOpen(false);
                    navigate('/tickets/quick', { state: buildQuickTicketState({ device }) });
                  }}
                >
                  <Smartphone className="h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-950">{device.displayName}</div>
                    <div className="truncate text-xs text-slate-500">{device.lastRepairSummary}</div>
                  </div>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="mt-3 w-full rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setDevicePickerOpen(false);
                navigate('/tickets/quick', {
                  state: buildQuickTicketState({ newDevice: true }),
                });
              }}
            >
              + New Device
            </button>
            <button
              type="button"
              className="mt-2 w-full rounded-xl border border-slate-300 py-2 text-sm font-semibold"
              onClick={() => setDevicePickerOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
